/**
 * What a serve is worth, and what a lapsed ticket costs.
 *
 * Every number here comes out of `timings.json` (starting points, tuned by
 * playtest), except the
 * per-dish bonus, which lives beside the dish in `recipes.json`. There is no
 * numeric literal in this file that a playtest could want to change — tuning
 * the game is a file edit and a restart, never a patch.
 *
 * The speed bonus is measured against the *ticket's own* validity window rather
 * than against the slack keys it was derived from — two tickets in one round
 * have different-length fuses — so a stored log can be rescored offline even
 * after the file has been retuned: the order carries both of its stamps, so the
 * denominator travels with the event rather than living in a config the
 * analysis would have to guess at ("recompute scoring offline from the log
 * without replaying").
 */

import type { Order } from '@overcooked/shared'
import type { Content, ScoringContent } from './content'

export function scoringOf(content: Content): ScoringContent {
  return content.timings.scoring
}

/** Ticks left on a ticket at the moment it was filled. Never negative. */
export function ticketTicksRemaining(order: Order, atTick: number): number {
  return Math.max(0, order.expiresAtTick - atTick)
}

/**
 * 0..1 of the ticket's life still unspent. A ticket filled the instant it
 * appeared scores the full bonus; one filled as the fuse burns out scores none.
 */
export function freshness(order: Order, atTick: number): number {
  const total = order.expiresAtTick - order.spawnedAtTick
  if (total <= 0) return 0
  return Math.min(1, ticketTicksRemaining(order, atTick) / total)
}

/**
 * The extra points `recipes.json` gives a dish for being harder to make
 * (`dishes[].bonus`). Absent, or a dish the recipes do not name, is 0.
 */
export function dishBonus(content: Content, dishId: string): number {
  return content.recipes.dishes.find((d) => d.id === dishId)?.bonus ?? 0
}

/**
 * Base, plus the ticket's dish bonus, plus the speed bonus rounded to a whole
 * point so the HUD never has to show a decimal and the log never carries
 * floating-point noise.
 *
 * The total travels in `SERVED.scoreDelta`, and replays and log checks sum
 * those deltas rather than calling this again, so a log recorded before a
 * bonus changed keeps the score it was played for.
 */
export function scoreForServe(content: Content, order: Order, atTick: number): number {
  const { baseScorePerOrder, maxSpeedBonus } = scoringOf(content)
  const bonus = dishBonus(content, order.dishId)
  return baseScorePerOrder + bonus + Math.round(maxSpeedBonus * freshness(order, atTick))
}

/** A plate nobody ordered. Legal, and worth whatever the file says it is. */
export function scoreForUnmatchedServe(content: Content): number {
  return scoringOf(content).unmatchedServePenalty
}

/** Already negative in the file; returned as-is so the sign lives in one place. */
export function scoreForExpiry(content: Content): number {
  return scoringOf(content).expiredOrderPenalty
}
